<script>
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    WootDropdownItem,
    WootDropdownMenu,
    Avatar,
    Icon,
    NextButton,
  },

  props: {
    options: {
      type: Array,
      default: () => [],
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
    hasThumbnail: {
      type: Boolean,
      default: true,
    },
    inputPlaceholder: {
      type: String,
      default: 'Search',
    },
    noSearchResult: {
      type: String,
      default: 'No results found',
    },
    hideSearch: {
      type: Boolean,
      default: false,
    },
    maxHeight: {
      type: String,
      default: '10rem',
    },
  },
  emits: ['select'],

  data() {
    return {
      search: '',
    };
  },

  computed: {
    filteredOptions() {
      if (this.hideSearch) return this.options;
      if (!this.search) return this.options;
      return this.options.filter(option => {
        if (option.isGroup) return false;
        return option.name.toLowerCase().includes(this.search.toLowerCase());
      });
    },
    noResult() {
      return (
        this.filteredOptions.filter(o => !o.isGroup).length === 0 &&
        this.search !== ''
      );
    },
  },

  mounted() {
    if (!this.hideSearch) {
      this.focusInput();
    }
  },

  methods: {
    onclick(option) {
      this.$emit('select', option);
    },
    focusInput() {
      this.$refs.searchbar?.focus();
    },
    isActive(option) {
      return this.selectedItems.some(item => item && option.id === item.id);
    },
  },
};
</script>

<template>
  <div class="dropdown-wrap">
    <div
      v-if="!hideSearch"
      class="flex-auto flex-grow-0 flex-shrink-0 mb-2 max-h-8"
    >
      <input
        ref="searchbar"
        v-model="search"
        type="text"
        class="search-input"
        autofocus="true"
        :placeholder="inputPlaceholder"
      />
    </div>
    <div class="flex items-start justify-start flex-auto overflow-auto mt-2">
      <div class="w-full" :style="{ maxHeight: maxHeight }">
        <WootDropdownMenu>
          <WootDropdownItem v-for="option in filteredOptions" :key="option.id">
            <div
              v-if="option.isGroup"
              class="px-2.5 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-n-slate-9 select-none pointer-events-none"
            >
              {{ option.name }}
            </div>
            <NextButton
              v-else
              slate
              :variant="isActive(option) ? 'faded' : 'ghost'"
              trailing-icon
              :icon="isActive(option) ? 'i-lucide-check' : ''"
              class="w-full !px-2.5"
              @click="() => onclick(option)"
            >
              <div
                class="flex items-center justify-between w-full min-w-0 gap-2"
              >
                <div class="flex items-center gap-2 overflow-hidden">
                  <span
                    v-if="option.color && !option.icon"
                    class="w-2 h-2 rounded-full flex-shrink-0"
                    :style="{ backgroundColor: option.color }"
                  />
                  <span
                    class="my-0 overflow-hidden text-sm leading-4 whitespace-nowrap text-ellipsis"
                    :title="option.name"
                  >
                    {{ option.name }}
                  </span>
                </div>
              </div>
              <Avatar
                v-if="hasThumbnail && !option.icon"
                :src="option.thumbnail"
                :icon-name="option.icon"
                :name="option.name"
                :status="option.availability_status"
                :size="24"
                hide-offline-status
                rounded-full
                :custom-avatar-color="option.icon ? option.color : null"
                :custom-avatar-bg="option.icon ? 'transparent' : null"
              />
              <Icon
                v-if="option.icon"
                :icon="option.icon"
                class="size-5 text-n-slate-11"
              />
            </NextButton>
          </WootDropdownItem>
        </WootDropdownMenu>
        <h4
          v-if="noResult"
          class="w-full justify-center items-center flex text-n-slate-10 py-2 px-2.5 overflow-hidden whitespace-nowrap text-ellipsis text-sm"
        >
          {{ noSearchResult }}
        </h4>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.dropdown-wrap {
  @apply w-full flex flex-col max-h-[12.5rem];
}

.search-input {
  @apply m-0 w-full border border-solid border-transparent h-8 text-sm text-n-slate-12 rounded-md focus:border-n-brand bg-n-background dark:bg-n-background;
}

.multiselect-dropdown--item {
  @apply justify-between w-full;

  &.active {
    @apply bg-n-slate-2 dark:bg-n-solid-3 border-n-weak/50 dark:border-n-weak font-medium;
  }

  &:hover {
    @apply bg-n-slate-2 dark:bg-n-solid-3 text-n-slate-12;
  }
}
</style>
